import React, { useState } from "react";
import { ShieldAlert, Check, X, Loader2 } from "lucide-react";
import { confirmAction } from "../../utils/API";

interface PendingOperation {
  collectionName: string;
  op: string;
  label?: string;
  dataPreview?: string;
}

interface ConfirmationCardProps {
  pendingActionId: string;
  operations: PendingOperation[];
  conversationId?: string;
  aiSettingId?: string;
  onConfirm: (pendingActionId: string) => void;
  onCancel: (pendingActionId: string) => void;
}

const ConfirmationCard: React.FC<ConfirmationCardProps> = ({
  pendingActionId,
  operations,
  conversationId,
  aiSettingId,
  onConfirm,
  onCancel,
}) => {
  const [loading, setLoading] = useState<"confirm" | "cancel" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setLoading("confirm");
    setError(null);
    // Do NOT call confirmAction API here — the parent (Chatbot.tsx) handles the SSE stream via fetch.
    // Calling Axios here would fire a second request that always gets 410 because pendingAction is already consumed.
    onConfirm(pendingActionId);
  };

  const handleCancel = async () => {
    setLoading("cancel");
    setError(null);
    try {
      await confirmAction({
        pendingActionId,
        confirmed: false,
        conversationId,
        aiSettingId,
      });
      onCancel(pendingActionId);
    } catch (err: any) {
      // Cancel failed on server — show error, don't silently dismiss
      setError("Failed to cancel on server. The action may still be pending.");
      setLoading(null);
    }
  };

  const opLabel = (op: string) => {
    switch (op) {
      case "create":
        return "Create";
      case "update":
        return "Update";
      case "delete":
        return "Delete";
      default:
        return op.charAt(0).toUpperCase() + op.slice(1);
    }
  };

  const opColor = (op: string, label?: string) => {
    if (/remove/i.test(label || "")) {
      return "text-red-600 bg-red-50 border-red-100";
    }

    switch (op) {
      case "delete":
        return "text-red-600 bg-red-50 border-red-100";
      case "update":
        return "text-amber-600 bg-amber-50 border-amber-100";
      case "create":
        return "text-emerald-600 bg-emerald-50 border-emerald-100";
      default:
        return "text-gray-600 bg-gray-50 border-gray-100";
    }
  };

  const previewEntries = (preview?: string) => {
    if (!preview) return [];
    try {
      const parsed = JSON.parse(preview);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return [];
      return Object.entries(parsed).map(([key, value]) => ({
        key,
        value: Array.isArray(value)
          ? value.join(", ")
          : value && typeof value === "object"
            ? JSON.stringify(value)
            : String(value ?? "-"),
      }));
    } catch {
      return [];
    }
  };

  return (
    <div className="w-full min-w-0 max-w-[520px] space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 pb-3">
        <div className="flex items-center gap-2.5">
          <div className="bg-red-50 p-1.5 rounded-lg text-red-500">
            <ShieldAlert size={16} />
          </div>
          <p className="text-sm font-bold text-gray-800 tracking-tight">
            Confirm Action
          </p>
        </div>
        <span className="text-[10px] font-bold px-2 py-0.5 bg-red-50 text-red-600 rounded-full border border-red-100 uppercase tracking-wider">
          Requires Approval
        </span>
      </div>

      {/* Warning */}
      <p className="text-xs text-gray-500 leading-relaxed bg-amber-50 p-2.5 rounded-xl border border-amber-100">
        The AI wants to perform the following operations. Please review before confirming.
      </p>

      {/* Operation list */}
      <div className="space-y-2">
        {operations.map((op, idx) => {
          const fields = previewEntries(op.dataPreview);

          return (
            <div
              key={idx}
              className={`min-w-0 rounded-xl border px-3 py-2.5 text-xs font-medium ${opColor(op.op, op.label)}`}
            >
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="shrink-0 rounded-md bg-white/70 px-2 py-1 text-[10px] font-bold uppercase tracking-wider">
                  {opLabel(op.op)}
                </span>
                <span className="min-w-0 flex-1 break-words text-sm font-semibold normal-case tracking-normal text-gray-800">
                  {op.label || `${opLabel(op.op)} ${op.collectionName}`}
                </span>
                <span className="shrink-0 rounded-full border border-gray-200 bg-white/70 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                  {op.collectionName}
                </span>
              </div>

              {fields.length > 0 ? (
                <div className="mt-2 grid gap-1.5 rounded-lg border border-white/80 bg-white/60 p-2">
                  {fields.map((field) => (
                    <div key={field.key} className="grid min-w-0 grid-cols-[minmax(80px,0.4fr)_minmax(0,1fr)] gap-2">
                      <span className="break-words font-semibold text-gray-500">{field.key}</span>
                      <span className="break-all text-gray-700">{field.value}</span>
                    </div>
                  ))}
                </div>
              ) : op.dataPreview ? (
                <p className="mt-2 break-all rounded-lg border border-white/80 bg-white/60 p-2 text-gray-600">
                  {op.dataPreview}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg border border-red-100">
          {error}
        </p>
      )}

      {/* Buttons */}
      <div className="flex gap-2 pt-1">
        <button
          onClick={handleConfirm}
          disabled={loading !== null}
          className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-bold shadow-md hover:bg-emerald-700 active:translate-y-0 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Confirm action"
        >
          {loading === "confirm" ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Check size={14} />
          )}
          <span>Confirm</span>
        </button>
        <button
          onClick={handleCancel}
          disabled={loading !== null}
          className="flex-1 py-2.5 bg-red-600 text-white rounded-xl text-sm font-bold shadow-md hover:bg-red-700 active:translate-y-0 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Cancel action"
        >
          {loading === "cancel" ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <X size={14} />
          )}
          <span>Cancel</span>
        </button>
      </div>
    </div>
  );
};

export default ConfirmationCard;
